import { HttpStatusCode } from "axios";
import { getApiErrorStatus } from "_shared/_errors/api-error-message";

/**
 * What to tell a user whose earlier conversation could not be opened.
 *
 * 404 is the one the panel meets: the conversation was deleted in another tab, or it expired after
 * its retention period. It is gone from the user's point of view. 403 is a conversation that is not
 * the caller's, which the panel only reaches when the session changed underneath it.
 */
export function describeConversationReadFailure(error: unknown): string {
  const status = getApiErrorStatus(error);

  if (status === HttpStatusCode.NotFound) {
    return "That conversation is no longer available. It may have been deleted.";
  }

  if (status === HttpStatusCode.Forbidden) {
    return "That conversation is not yours to open.";
  }

  return "That conversation could not be opened. Please try again.";
}

/**
 * What to tell a user whose conversation was not deleted.
 *
 * 409 is a legal hold. It outranks the user's request, and it is not something trying again
 * will change, so the message says the conversation is kept rather than that deleting failed.
 */
export function describeConversationDeleteFailure(error: unknown): string {
  const status = getApiErrorStatus(error);

  if (status === HttpStatusCode.Conflict) {
    return "This conversation is preserved and cannot be deleted.";
  }

  if (status === HttpStatusCode.NotFound) {
    return "That conversation is no longer available. It may already have been deleted.";
  }

  if (status === HttpStatusCode.Forbidden) {
    return "That conversation is not yours to delete.";
  }

  return "That conversation could not be deleted. Please try again.";
}
